import React from 'react';
import '../styles/DealFeedTable.css';
import { Link } from 'react-router-dom';
import { formatterLong, numberFormatter } from '../styles/GlobalStyles';

const PROPERTY_TYPE_LABELS = {
    single_family: 'Single Family', multi_family: 'Multi-Family',
    townhouse: 'Townhouse', condo: 'Condo', mobile: 'Mobile',
    land: 'Land', portfolio: 'Portfolio',
};

const formatPrice = (value) => (value == null ? '—' : formatterLong.format(value).replace(/\.00$/, ''));
const formatNum = (value) => (value == null ? '—' : numberFormatter.format(value));

/**
 * Read-only table view of the public deal feed. Renders only buyer-safe
 * fields already present in the sanitized payload — no profit/repair/ROI/
 * contact/status columns, no inline editing.
 *
 * Props:
 *  - deals: sanitized deal objects (from getPublicDealFeed)
 *  - companySlug, buyerId: used to build the per-row detail link
 */
function DealFeedTable({ deals = [], companySlug, buyerId, feedSearch = '' }) {
    // ARV is a flip metric — drop the column entirely when every deal shown is
    // co-living (it would just be a column of dashes). Any flip deal brings it back.
    const isCoLivingDeal = (d) => d.strategy === 'coLiving' && !!d.coLiving;
    const hideArv = deals.length > 0 && deals.every(isCoLivingDeal);
    return (
        <div className="deal-feed-table-container">
            <table className="deal-feed-table">
                <thead>
                    <tr>
                        <th className="deal-feed-table-th label-bold-caps text-align-left">Address</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-left">Type</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Beds</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Baths</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Sq Ft</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Year</th>
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Asking</th>
                        {!hideArv && <th className="deal-feed-table-th label-bold-caps text-align-right">ARV</th>}
                        <th className="deal-feed-table-th label-bold-caps text-align-right">Match</th>
                    </tr>
                </thead>
                <tbody>
                    {deals.map((deal, i) => {
                        const addr = deal.address || {};
                        const cityLine = [addr.city, addr.state].filter(Boolean).join(', ')
                            + (addr.zipcode ? ` ${addr.zipcode}` : '');
                        // Co-living deals — badge the Type cell and show projected beds
                        // (current → projected). Same gate + headline as the card.
                        const isCoLiving = deal.strategy === 'coLiving' && !!deal.coLiving;
                        const projectedBeds = isCoLiving ? (deal.coLiving.projectedBeds ?? null) : null;
                        const bedsLabel = (isCoLiving && projectedBeds != null && projectedBeds !== deal.bedrooms)
                            ? `${formatNum(deal.bedrooms)} → ${projectedBeds}`
                            : formatNum(deal.bedrooms);
                        return (
                            <tr key={deal.leadId || i} className="deal-feed-table-row">
                                <td className="deal-feed-table-td">
                                    <Link
                                        to={buyerId ? `/deals/${companySlug}/${deal.leadId}/${buyerId}` : `/deals/${companySlug}/${deal.leadId}`}
                                        state={feedSearch ? { feedSearch } : undefined}
                                        className="deal-feed-table-address-link no-underline"
                                    >
                                        <span className="deal-feed-table-street body-semibold colour-primary">
                                            {addr.streetAddress || 'Address unavailable'}
                                        </span>
                                        <span className="deal-feed-table-city label-regular colour-secondary">
                                            {cityLine}
                                        </span>
                                    </Link>
                                </td>
                                <td className="deal-feed-table-td body-regular colour-primary">
                                    {isCoLiving && <span className="deal-feed-table-coliving-badge label-bold">Co-Living</span>}
                                    {deal.propertyType ? (PROPERTY_TYPE_LABELS[deal.propertyType] || deal.propertyType) : (isCoLiving ? '' : '—')}
                                </td>
                                <td className="deal-feed-table-td text-align-right body-regular colour-primary">{bedsLabel}</td>
                                <td className="deal-feed-table-td text-align-right body-regular colour-primary">{formatNum(deal.bathrooms)}</td>
                                <td className="deal-feed-table-td text-align-right body-regular colour-primary">{formatNum(deal.squareFeet)}</td>
                                <td className="deal-feed-table-td text-align-right body-regular colour-primary">{deal.yearBuilt ?? '—'}</td>
                                <td className="deal-feed-table-td text-align-right body-semibold colour-primary">{formatPrice(deal.askingPrice)}</td>
                                {!hideArv && <td className="deal-feed-table-td text-align-right body-regular colour-primary">{formatPrice(deal.arv)}</td>}
                                <td className="deal-feed-table-td text-align-right body-regular colour-primary">
                                    {deal.matchPercentage != null ? `${Math.round(deal.matchPercentage * 100)}%` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default DealFeedTable;
